import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI, postsAPI } from '../services/api';
import { subscribePush, isPushSupported } from '../services/pushNotifications';
import { generateKeyPair, getStoredPublicKey } from '../utils/encryption';
import { encryptionAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeStreams, setActiveStreams] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      setActiveStreams([]);
      return;
    }

    const fetchActiveStreams = async () => {
      try {
        const res = await postsAPI.getActiveLiveStreams();
        setActiveStreams(res.data?.activeStreams || []);
      } catch (err) {
        console.error("Failed to fetch active streams:", err);
      }
    };

    fetchActiveStreams();
    const interval = setInterval(fetchActiveStreams, 10000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const initEncryptionKeys = async (userId) => {
    if (!userId) return;
    try {
      let pubKey = await getStoredPublicKey(userId);
      if (!pubKey) pubKey = await generateKeyPair(userId);
      await encryptionAPI.storePublicKey(pubKey);
    } catch {}
  };

  const login = useCallback(async (email, password, totpCode = null) => {
    try {
      const res = await authAPI.login(email, password, totpCode);
      
      if (res.data.requires2FA) {
        return { success: false, requires2FA: true, message: res.data.message };
      }

      const { token, refreshToken, user: userData } = res.data;
      localStorage.setItem('veonfox_token', token);
      if (refreshToken) localStorage.setItem('veonfox_refresh', refreshToken);
      localStorage.setItem('veonfox_auth', 'true');
      setUser(userData);
      setIsAuthenticated(true);
      if (isPushSupported()) subscribePush().catch(() => {});
      initEncryptionKeys(userData.id).catch(() => {});
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Login failed' };
    }
  }, []);

  const register = useCallback(async (email, password, displayName, username, referralCode = null) => {
    try {
      const res = await authAPI.register(email, password, displayName, username, referralCode);
      const { token, refreshToken, user: userData } = res.data;

      localStorage.setItem('veonfox_token', token);
      if (refreshToken) localStorage.setItem('veonfox_refresh', refreshToken);
      localStorage.setItem('veonfox_auth', 'true');
      setUser(userData);
      setIsAuthenticated(true);
      if (isPushSupported()) subscribePush().catch(() => {});
      initEncryptionKeys(userData.id).catch(() => {});
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Registration failed' };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch {
      // Logout even if server call fails
    }
    localStorage.removeItem('veonfox_token');
    localStorage.removeItem('veonfox_refresh');
    localStorage.removeItem('veonfox_auth');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
    } catch (err) {
      // ignore
    }
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const tg = window.Telegram?.WebApp;
      if (tg && tg.initData) {
        try {
          const res = await authAPI.authTelegram(tg.initData);
          const { token, refreshToken, user: userData } = res.data;
          localStorage.setItem('veonfox_token', token);
          if (refreshToken) localStorage.setItem('veonfox_refresh', refreshToken);
          localStorage.setItem('veonfox_auth', 'true');
          setUser(userData);
          setIsAuthenticated(true);
          initEncryptionKeys(userData.id).catch(() => {});
          setLoading(false);
          return;
        } catch (err) {
          console.error("Telegram WebApp authentication failed:", err);
        }
      }

      const token = localStorage.getItem('veonfox_token');
      if (token) {
        try {
          const res = await authAPI.getMe();
          setUser(res.data);
          setIsAuthenticated(true);
          initEncryptionKeys(res.data.id).catch(() => {});
        } catch {
          localStorage.removeItem('veonfox_token');
          localStorage.removeItem('veonfox_auth');
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const setAuthData = useCallback((userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
    if (isPushSupported()) subscribePush().catch(() => {});
    initEncryptionKeys(userData.id).catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f0f2f5] dark:bg-[#0b0e14] flex items-center justify-center transition-colors duration-300">
        <div className="w-10 h-10 border-3 border-[#38bdf8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, isAuthenticated, login, register, logout, refreshUser, setAuthData, activeStreams }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
